import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Star, X, Camera, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { db, storage } from '@/lib/firebase'
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useAuthState } from '@/hooks/useAuth'
import { AuthModal } from '@/components/auth/AuthModal'
import type { Artist, UnrealArtist, ArtistRating } from '@/types/models'

interface RatingCategory {
  id: keyof Pick<ArtistRating, 'skillRating' | 'vibeRating' | 'professionalismRating'>
  title: string
  description: string
  value: number
}

export default function RateArtistPage() {
  const { } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { artistType, artistId } = useParams<{ artistType: 'real' | 'unreal', artistId: string }>()
  const { user, loading: authLoading } = useAuthState()
  
  const [artist, setArtist] = useState<Artist | UnrealArtist | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form state
  const [ratings, setRatings] = useState<RatingCategory[]>([
    { id: 'skillRating', title: 'Skill', description: 'Quality of lines, shading, overall technical ability', value: 0 },
    { id: 'vibeRating', title: 'Vibe', description: 'Friendliness, experience during session, atmosphere', value: 0 },
    { id: 'professionalismRating', title: 'Professionalism', description: 'Communication, reliability, hygiene, punctuality', value: 0 }
  ])
  
  const [reviewText, setReviewText] = useState('')
  const [reviewImages, setReviewImages] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  
  // Modal states
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [uploadingImages, setUploadingImages] = useState(false)

  // Load form data from URL params
  useEffect(() => {
    const skillRating = parseInt(searchParams.get('skill') || '0')
    const vibeRating = parseInt(searchParams.get('vibe') || '0')
    const professionalismRating = parseInt(searchParams.get('professionalism') || '0')
    const reviewTextParam = searchParams.get('review') || ''
    
    if (skillRating || vibeRating || professionalismRating || reviewTextParam) {
      setRatings(prev => prev.map(rating => {
        if (rating.id === 'skillRating') return { ...rating, value: skillRating }
        if (rating.id === 'vibeRating') return { ...rating, value: vibeRating }
        if (rating.id === 'professionalismRating') return { ...rating, value: professionalismRating }
        return rating
      }))
      setReviewText(reviewTextParam)
    }
  }, [searchParams])

  // Update URL params when form data changes
  useEffect(() => {
    const params = new URLSearchParams()
    ratings.forEach(rating => {
      if (rating.value > 0) {
        const paramName = rating.id.replace('Rating', '')
        params.set(paramName, rating.value.toString())
      }
    })
    if (reviewText.trim()) {
      params.set('review', reviewText)
    }
    
    // Only update if params actually changed
    const newParamsString = params.toString()
    const currentParamsString = searchParams.toString()
    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true })
    }
  }, [ratings, reviewText, setSearchParams])

  useEffect(() => {
    if (!artistType || !artistId) {
      navigate('/board')
      return
    }

    fetchArtistData()
  }, [artistType, artistId, navigate])

  const fetchArtistData = async () => {
    try {
      setLoading(true)
      setError(null)

      let artistData: Artist | UnrealArtist | null = null
      
      if (artistType === 'real') {
        const artistDoc = await getDoc(doc(db, 'artists', artistId!))
        if (artistDoc.exists()) {
          artistData = { id: artistDoc.id, ...artistDoc.data() } as Artist
        }
      } else {
        const artistDoc = await getDoc(doc(db, 'unrealArtists', artistId!))
        if (artistDoc.exists()) {
          artistData = { id: artistDoc.id, ...artistDoc.data() } as UnrealArtist
        }
      }

      if (!artistData) {
        setError('Artist not found')
        return
      }

      setArtist(artistData)
    } catch (error) {
      console.error('Error fetching artist data:', error)
      setError('Failed to load artist data')
    } finally {
      setLoading(false)
    }
  }

  const handleStarClick = (categoryId: string, starValue: number) => {
    setRatings(prev => prev.map(rating => 
      rating.id === categoryId ? { ...rating, value: starValue } : rating
    ))
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const validFiles: File[] = []
    const newPreviews: string[] = []

    files.forEach(file => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files')
        return
      }

      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 10MB`)
        return
      }

      validFiles.push(file)
      newPreviews.push(URL.createObjectURL(file))
    })

    if (reviewImages.length + validFiles.length > 5) {
      alert('Maximum 5 images allowed')
      return
    }

    setReviewImages(prev => [...prev, ...validFiles])
    setImagePreviews(prev => [...prev, ...newPreviews])
  }

  const removeImage = (index: number) => {
    URL.revokeObjectURL(imagePreviews[index])
    setReviewImages(prev => prev.filter((_, i) => i !== index))
    setImagePreviews(prev => prev.filter((_, i) => i !== index))
  }

  const uploadImages = async (): Promise<string[]> => {
    if (reviewImages.length === 0) return []

    setUploadingImages(true)
    const uploadPromises = reviewImages.map(async (file, index) => {
      const fileName = `review-images/${Date.now()}-${index}-${file.name}`
      const storageRef = ref(storage, fileName)
      await uploadBytes(storageRef, file)
      return getDownloadURL(storageRef)
    })

    try {
      const imageUrls = await Promise.all(uploadPromises)
      return imageUrls
    } finally {
      setUploadingImages(false)
    }
  }

  const handleSubmit = async () => {
    // Validate ratings
    const hasAllRatings = ratings.every(rating => rating.value > 0)
    if (!hasAllRatings) {
      alert('Please provide ratings for all categories')
      return
    }

    // Validate review text
    if (!reviewText.trim()) {
      alert('Please provide a detailed review')
      return
    }

    if (!user) {
      setShowAuthModal(true)
      return
    }

    setShowConfirmModal(true)
  }

  const handleConfirmSubmit = async () => {
    if (!agreeToTerms) {
      alert('Please agree to the terms')
      return
    }

    try {
      setSubmitting(true)

      // Upload images first
      const imageUrls = await uploadImages()

      // Calculate overall rating
      const overallRating = ratings.reduce((sum, rating) => sum + rating.value, 0) / ratings.length

      // Create rating document
      const ratingData: Omit<ArtistRating, 'id'> = {
        artistId: artistId!,
        artistType: artistType as 'real' | 'unreal',
        userId: user!.uid,
        skillRating: ratings.find(r => r.id === 'skillRating')?.value || 0,
        vibeRating: ratings.find(r => r.id === 'vibeRating')?.value || 0,
        professionalismRating: ratings.find(r => r.id === 'professionalismRating')?.value || 0,
        overallRating: Math.round(overallRating * 10) / 10, // Round to 1 decimal
        reviewText: reviewText.trim(),
        ...(imageUrls.length > 0 && { reviewImages: imageUrls }),
        createdAt: new Date(),
        isVisible: true,
        isModerated: false,
        isAnonymous: isAnonymous
      }

      await addDoc(collection(db, 'artistRatings'), {
        ...ratingData,
        createdAt: serverTimestamp()
      })

      navigate('/board')
    } catch (error) {
      console.error('Error submitting rating:', error)
      alert('Failed to submit rating. Please try again.')
    } finally {
      setSubmitting(false)
      setShowConfirmModal(false)
    }
  }


  const getArtistDisplayName = (artist: Artist | UnrealArtist) => {
    if ('firstName' in artist && artist.firstName && artist.lastName) {
      return `${artist.firstName} ${artist.lastName}`
    }
    return artist.displayName || ('username' in artist ? artist.username : 'Unknown Artist')
  }

  const getArtistUsername = (artist: Artist | UnrealArtist) => {
    if ('username' in artist) {
      return artist.username
    }
    return artist.displayName
  }

  const renderStars = (categoryId: string, currentValue: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-8 w-8 cursor-pointer transition-colors ${
              star <= currentValue 
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-gray-300 hover:text-yellow-200'
            }`}
            onClick={() => handleStarClick(categoryId, star)}
          />
        ))}
      </div>
    )
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !artist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">Artist not found</p>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/board')}>
            Back to Search
          </Button>
        </div>
      </div>
    )
  }

  const artistDisplayName = getArtistDisplayName(artist)
  const artistUsername = getArtistUsername(artist)

  return (
    <>
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold">Rate Artist</h1>
              <p className="text-sm text-muted-foreground">
                @{artistUsername}
              </p>
            </div>
          </div>

          {/* Artist Info */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="text-center">
                <h2 className="text-lg font-bold mb-1">{artistDisplayName}</h2>
                <p className="text-muted-foreground">@{artistUsername}</p>
              </div>
            </CardContent>
          </Card>

          {/* Rating Form */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Rate Your Experience</CardTitle>
              <p className="text-sm text-muted-foreground">
                Rate this artist and share your experience
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Rating Categories */}
              {ratings.map((rating) => (
                <div key={rating.id} className="space-y-2">
                  <div>
                    <h3 className="font-medium">{rating.title}</h3>
                    <p className="text-sm text-muted-foreground">{rating.description}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {renderStars(rating.id, rating.value)}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {rating.value > 0 ? `${rating.value}/5` : 'Tap to rate'}
                    </span>
                  </div>
                </div>
              ))}

              {/* Divider */}
              <div className="border-t border-border"></div>

              {/* Review Text */}
              <div className="space-y-2">
                <Label htmlFor="review-text">Detailed Review *</Label>
                <Textarea
                  id="review-text"
                  placeholder="Share your experience with this artist..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={4}
                  className="resize-none"
                  required
                />
              </div>

              {/* Image Upload */}
              <div className="space-y-3">
                <div>
                  <Label>Photos (Optional)</Label>
                  <p className="text-sm text-muted-foreground">
                    Upload photos of your tattoo work. Max 5 images, 10MB each.
                  </p>
                </div>
                
                {/* Upload Button */}
                <div className="flex items-center justify-center">
                  <Label htmlFor="image-upload" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                      <Camera className="h-5 w-5" />
                      <span>Add Photos</span>
                    </div>
                  </Label>
                  <Input
                    id="image-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>

                {/* Image Previews */}
                {imagePreviews.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0"
                          onClick={() => removeImage(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            onClick={handleSubmit}
            disabled={submitting || ratings.some(r => r.value === 0) || !reviewText.trim()}
            className="w-full mb-6"
            size="lg"
          >
            {submitting ? 'Posting Review...' : 'Post Review'}
          </Button>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onOpenChange={setShowAuthModal}
        onSuccess={() => {
          setShowAuthModal(false)
          setShowConfirmModal(true)
        }}
        defaultMode="signup"
        preAuthMessage="Please create an account to submit your review"
        showPreAuth={true}
      />

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="anonymous" 
                checked={isAnonymous}
                onCheckedChange={(checked) => setIsAnonymous(checked as boolean)}
              />
              <Label htmlFor="anonymous" className="text-sm">
                Anonymous (only use if necessary)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="terms" 
                checked={agreeToTerms}
                onCheckedChange={(checked) => setAgreeToTerms(checked as boolean)}
              />
              <Label htmlFor="terms" className="text-sm">
                I confirm this review is truthful and accurate. We may ask for proof of your tattoo work.
              </Label>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmSubmit}
                disabled={!agreeToTerms || submitting || uploadingImages}
                className="flex-1"
              >
                {submitting || uploadingImages ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {uploadingImages ? 'Uploading...' : 'Submitting...'}
                  </div>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Submit Review
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
